import * as React from 'react';
import { CheckCheck } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import {
  SegmentedControl,
  WorkspaceButton,
  WorkspaceField,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import type {
  Disposition,
  DispositionRow,
  VendorIntelligence,
} from '@/lib/api/vendor-intelligence';
import { cn } from '@/lib/utils/cn';
import {
  DISPOSITION_CHOICES,
  DISPOSITION_LABEL,
  MIN_DISPOSITION_REASON_CHARS,
  dispositionRows,
  formatDate,
  normaliseReason,
  plural,
  proposedDispositionRows,
  reasonNeeded,
  type DispositionRowView,
} from './vi-logic';

/** What the four words mean for the report, said once above the list. */
export const DISPOSITION_COPY =
  'Shortlisted vendors get a page in the report; alternates and set-aside vendors are listed with your reason; not-assessed vendors are named only. Changing a disposition never changes a rating or the index.';

interface Editing {
  productId: string;
  choice: Disposition;
  reason: string;
}

/**
 * The disposition list of the Shortlist step (T-0274; E24-design §3 item 3;
 * ADR-026 decision 3): one row per vendor in rank order with its index and
 * coverage, the disposition code proposed (the top three shortlisted, the
 * other ranked vendors alternates, the unrated ones not assessed), and
 * whether a consultant has confirmed it. Confirm shortlist confirms every
 * proposed row in one write. Picking another disposition opens a reason
 * under the row, needed at ten characters, and saves that row alone.
 *
 * A vendor with no confirmed rating is shown as not assessed and cannot
 * be shortlisted: its ratings come first. Every figure is the server's.
 */
export function DispositionList({
  evaluation,
  canEdit,
  busy,
  error,
  onConfirmAll,
  onSetOne,
}: {
  evaluation: VendorIntelligence;
  canEdit: boolean;
  busy: boolean;
  error: unknown;
  /** Confirm shortlist: every proposed row, one write. */
  onConfirmAll: (rows: DispositionRow[]) => void;
  /** One row with its reason; resolves once the server has it. */
  onSetOne: (row: DispositionRow) => Promise<unknown>;
}) {
  const rows = React.useMemo(() => dispositionRows(evaluation), [evaluation]);
  const proposed = React.useMemo(() => proposedDispositionRows(evaluation), [evaluation]);
  const [editing, setEditing] = React.useState<Editing | null>(null);
  const confirmed = evaluation.fit.dispositions_confirmed;
  const unproposed = rows.filter((r) => r.product.disposition === null).length;
  const trimmed = editing ? normaliseReason(editing.reason) : '';
  const reasonShort = trimmed.length < MIN_DISPOSITION_REASON_CHARS;

  const pick = (row: DispositionRowView, choice: Disposition) => {
    if (!canEdit || busy) return;
    // Back to what the row holds: nothing to save.
    if (!reasonNeeded(row.product, choice)) {
      setEditing(null);
      return;
    }
    setEditing({ productId: row.product.product_id, choice, reason: '' });
  };
  const save = () => {
    if (!editing || reasonShort) return;
    void onSetOne({
      product_id: editing.productId,
      disposition: editing.choice,
      reason: trimmed,
    }).then(
      () => setEditing(null),
      () => undefined,
    );
  };

  return (
    <section aria-labelledby="vi-dispositions" className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <h2 id="vi-dispositions" className="m-0 text-h3 font-semibold text-ink-900">
          Shortlist
        </h2>
        <span className="text-meta text-ink-500">
          {confirmed
            ? 'Every disposition is confirmed. Change any with a reason.'
            : 'Proposed from the ranking. Confirm it, or change any row with a reason.'}
        </span>
        {confirmed ? (
          <StatusChip tone="healthy" size="sm" className="ml-auto">
            Confirmed
          </StatusChip>
        ) : canEdit ? (
          <WorkspaceButton
            variant="primary"
            className="ml-auto"
            disabled={busy || proposed.length === 0}
            title={
              proposed.length === 0
                ? 'Nothing is proposed yet: code proposes dispositions once ratings are confirmed.'
                : undefined
            }
            onClick={() => onConfirmAll(proposed)}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Confirm shortlist
          </WorkspaceButton>
        ) : null}
      </div>
      <p className="m-0 text-body-sm text-ink-700">{DISPOSITION_COPY}</p>

      <div className="overflow-x-auto rounded-card border border-ink-100">
        <table className="w-full border-collapse text-body-sm" aria-label="Dispositions">
          <thead>
            <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500">
              <th scope="col" className="px-3 py-2 font-medium">
                Rank
              </th>
              <th scope="col" className="min-w-[180px] px-3 py-2 font-medium">
                Vendor
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Index
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Coverage
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Disposition
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Status
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Reason
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const p = row.product;
              const isEditing = editing?.productId === p.product_id;
              const rated = row.fit !== null && row.fit.rank !== null;
              const held = p.disposition;
              const value: Disposition | null = isEditing ? editing.choice : held;
              const choice = DISPOSITION_CHOICES.find((c) => c === value) ?? null;
              const last = i === rows.length - 1 && !isEditing;
              return (
                <React.Fragment key={p.product_id}>
                  <tr className={cn('border-b border-ink-100 align-top', last && 'border-b-0')}>
                    <td className="px-3 py-2 font-mono tabular-nums text-ink-700">
                      {row.rank ?? '–'}
                    </td>
                    <td className="px-3 py-2">
                      <span className="block font-semibold text-ink-900">{p.name}</span>
                      <span className="block text-meta text-ink-500">{p.vendor.name}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-ink-700">
                      {rated ? row.fit!.index.toFixed(1) : '–'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-ink-700">
                      {rated ? `${row.fit!.coverage.toFixed(1)}%` : '–'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {rated ? (
                        <SegmentedControl
                          className="whitespace-nowrap"
                          aria-label={`Disposition of ${p.name}`}
                          size="sm"
                          readOnly={!canEdit || busy}
                          value={choice}
                          onChange={(next) => pick(row, next)}
                          options={DISPOSITION_CHOICES.map((d) => ({
                            value: d,
                            label: DISPOSITION_LABEL[d],
                          }))}
                        />
                      ) : (
                        <span
                          className="text-ink-700"
                          title="No confirmed rating yet: a vendor is assessed once its ratings are confirmed."
                        >
                          {held ? DISPOSITION_LABEL[held] : '–'}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {p.disposition_status === 'confirmed' ? (
                        <>
                          <StatusChip tone="healthy" size="sm">
                            Confirmed
                          </StatusChip>
                          {p.disposition_by || p.disposition_at ? (
                            <span className="mt-1 block text-meta text-ink-500">
                              {[p.disposition_by?.name, formatDate(p.disposition_at)]
                                .filter((s) => s)
                                .join(' · ')}
                            </span>
                          ) : null}
                        </>
                      ) : p.disposition_status === 'proposed' ? (
                        <StatusChip tone="amber" size="sm">
                          Proposed
                        </StatusChip>
                      ) : (
                        <StatusChip tone="unset" size="sm">
                          Not proposed yet
                        </StatusChip>
                      )}
                    </td>
                    <td className="max-w-[360px] px-3 py-2 text-ink-700">
                      {p.disposition_reason ? (
                        <span className="italic">{p.disposition_reason}</span>
                      ) : (
                        <span className="text-ink-300">–</span>
                      )}
                    </td>
                  </tr>
                  {isEditing ? (
                    <tr
                      className={cn(
                        'border-b border-ink-100 bg-paper-50',
                        i === rows.length - 1 && 'border-b-0',
                      )}
                    >
                      <td colSpan={7} className="px-3 py-2.5">
                        <div className="flex flex-wrap items-start gap-3">
                          <WorkspaceField
                            className="min-w-[320px] flex-1"
                            label="Reason"
                            help={`${DISPOSITION_LABEL[editing.choice]} for ${p.name}: at least ${MIN_DISPOSITION_REASON_CHARS} characters. It prints in the report and goes in the audit history.`}
                          >
                            {(id, describedBy) => (
                              <textarea
                                id={id}
                                aria-describedby={describedBy}
                                value={editing.reason}
                                onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                                rows={2}
                                autoFocus
                                className={cn(
                                  workspaceFieldClass,
                                  'min-h-[56px] resize-y py-2 leading-[20px]',
                                )}
                              />
                            )}
                          </WorkspaceField>
                          <div className="flex gap-2 pt-6">
                            <WorkspaceButton
                              variant="tertiary"
                              disabled={busy}
                              onClick={() => setEditing(null)}
                            >
                              Cancel
                            </WorkspaceButton>
                            <WorkspaceButton
                              variant="primary"
                              disabled={busy || reasonShort}
                              onClick={save}
                            >
                              Save and confirm
                            </WorkspaceButton>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {unproposed > 0 ? (
        <p className="m-0 text-meta text-ink-500">
          {plural(unproposed, 'vendor')} {unproposed === 1 ? 'has' : 'have'} no proposal yet: code
          proposes one when a rating is confirmed or the vendor scope changes. Choosing a
          disposition with a reason decides {unproposed === 1 ? 'it' : 'them'} now.
        </p>
      ) : null}
      {error ? <ErrorMessage error={error} /> : null}
    </section>
  );
}
